// Export Service for Word documents and photo downloads
import { Document, Packer, Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, WidthType, BorderStyle, AlignmentType } from 'docx'
import { saveAs } from 'file-saver'
import JSZip from 'jszip'
import { formatDate, generateReportFilename, generatePhotosZipFilename, sanitizeFilename, base64ToBlob } from '../utils/helpers'

/**
 * Generate a Word document report for a survey
 * @param {Object} survey - The survey object
 * @param {Array} photos - Array of photo objects
 * @param {Array} categories - Array of category objects
 */
export async function generateWordDocument(survey, photos, categories) {
  const analysisResult = survey.analysisResult || {}

  // Create document sections
  const children = []

  // Title
  children.push(
    new Paragraph({
      text: 'PROPERTY SURVEY REPORT',
      heading: HeadingLevel.TITLE,
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 }
    })
  )

  // Company name
  children.push(
    new Paragraph({
      text: 'Clystcore Surveyors',
      alignment: AlignmentType.CENTER,
      spacing: { after: 600 },
      children: [
        new TextRun({
          text: 'Clystcore Surveyors',
          bold: true,
          size: 32,
          color: '1e3a5f'
        })
      ]
    })
  )

  // Property Details Table
  children.push(
    new Paragraph({
      text: 'Property Details',
      heading: HeadingLevel.HEADING_1,
      spacing: { before: 400, after: 200 }
    })
  )

  children.push(createPropertyDetailsTable(survey))

  // Overall Summary
  if (analysisResult.overallSummary) {
    children.push(
      new Paragraph({
        text: 'Executive Summary',
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 400, after: 200 }
      })
    )
    children.push(
      new Paragraph({
        text: analysisResult.overallSummary,
        spacing: { after: 200 }
      })
    )
  }

  // Category Analysis Sections
  children.push(
    new Paragraph({
      text: 'Detailed Findings',
      heading: HeadingLevel.HEADING_1,
      spacing: { before: 400, after: 200 }
    })
  )

  for (const category of categories) {
    const categoryPhotos = photos.filter(p => p.categoryCode === category.code)
    const categoryAnalysis = analysisResult.categories?.[category.code]

    if (categoryPhotos.length > 0 || categoryAnalysis) {
      // Category heading
      children.push(
        new Paragraph({
          text: category.name,
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 300, after: 100 }
        })
      )

      // Photo count
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `Photos captured: ${categoryPhotos.length}`,
              italics: true,
              color: '666666'
            })
          ],
          spacing: { after: 100 }
        })
      )

      // Analysis
      if (categoryAnalysis?.analysis) {
        children.push(
          new Paragraph({
            text: categoryAnalysis.analysis,
            spacing: { after: 200 }
          })
        )
      } else if (categoryAnalysis?.error) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `Analysis unavailable: ${categoryAnalysis.error}`,
                italics: true,
                color: 'cc0000'
              })
            ],
            spacing: { after: 200 }
          })
        )
      } else {
        children.push(
          new Paragraph({
            text: 'No analysis available for this category.',
            spacing: { after: 200 }
          })
        )
      }
    }
  }

  // Recommendations
  if (analysisResult.recommendations) {
    children.push(
      new Paragraph({
        text: 'Recommendations',
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 400, after: 200 }
      })
    )
    children.push(
      new Paragraph({
        text: analysisResult.recommendations,
        spacing: { after: 200 }
      })
    )
  }

  // Footer
  children.push(
    new Paragraph({
      text: '',
      spacing: { before: 400 }
    })
  )
  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `Report generated: ${formatDate(new Date().toISOString())}`,
          size: 20,
          color: '999999'
        })
      ],
      alignment: AlignmentType.CENTER
    })
  )
  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: 'Powered by Clystcore Surveyors AI Analysis',
          size: 20,
          color: '999999'
        })
      ],
      alignment: AlignmentType.CENTER
    })
  )

  // Create document
  const doc = new Document({
    sections: [{
      properties: {},
      children: children
    }]
  })

  // Generate and download
  const blob = await Packer.toBlob(doc)
  const filename = generateReportFilename(survey)
  saveAs(blob, filename)

  return filename
}

/**
 * Create property details table
 */
function createPropertyDetailsTable(survey) {
  return new Table({
    width: {
      size: 100,
      type: WidthType.PERCENTAGE
    },
    rows: [
      createTableRow('Property Name', survey.propertyName),
      createTableRow('Address', survey.address),
      createTableRow('Postcode', survey.postcode),
      createTableRow('Surveyor', survey.surveyorName),
      createTableRow('Survey Date', formatDate(survey.createdAt)),
      createTableRow('Status', survey.status.charAt(0).toUpperCase() + survey.status.slice(1))
    ]
  })
}

/**
 * Create a table row with label and value
 */
function createTableRow(label, value) {
  return new TableRow({
    children: [
      new TableCell({
        width: {
          size: 30,
          type: WidthType.PERCENTAGE
        },
        children: [
          new Paragraph({
            children: [
              new TextRun({
                text: label,
                bold: true
              })
            ]
          })
        ],
        shading: {
          fill: 'f1f5f9'
        }
      }),
      new TableCell({
        width: {
          size: 70,
          type: WidthType.PERCENTAGE
        },
        children: [
          new Paragraph({
            text: value || 'N/A'
          })
        ]
      })
    ]
  })
}

const WORKER_URL = 'https://clystcore-api.js-55b.workers.dev'

/**
 * Download all survey photos as a ZIP file organized by category
 * @param {Object} survey - The survey object
 * @param {Array} photos - Array of photo objects
 * @param {Array} categories - Array of category objects
 */
export async function downloadPhotosAsZip(survey, photos, categories) {
  const zip = new JSZip()

  // Create folder structure by category
  for (const category of categories) {
    const categoryPhotos = photos.filter(p => p.categoryCode === category.code)

    if (categoryPhotos.length > 0) {
      const folderName = sanitizeFilename(category.name)
      const folder = zip.folder(folderName)

      for (let i = 0; i < categoryPhotos.length; i++) {
        const photo = categoryPhotos[i]
        const filename = `${folderName}_${String(i + 1).padStart(3, '0')}.jpg`

        try {
          let imageBlob = null

          // Use local cached imageData if available
          if (photo.imageData) {
            imageBlob = base64ToBlob(photo.imageData)
          } else {
            // Fetch from R2 - imageUrl is already a full URL
            const imageUrl = photo.imageUrl.startsWith('http')
              ? photo.imageUrl
              : `${WORKER_URL}${photo.imageUrl}`
            const response = await fetch(imageUrl)
            if (response.ok) {
              imageBlob = await response.blob()
            }
          }

          if (imageBlob) {
            folder.file(filename, imageBlob)
          }
        } catch (error) {
          console.error(`Failed to fetch photo ${photo.id}:`, error)
        }
      }
    }
  }

  // Add survey info file
  const surveyInfo = `Property Survey Photos
======================

Property: ${survey.propertyName}
Address: ${survey.address}
Postcode: ${survey.postcode}
Surveyor: ${survey.surveyorName}
Date: ${formatDate(survey.createdAt)}

Total Photos: ${photos.length}

Categories:
${categories.map(c => {
  const count = photos.filter(p => p.categoryCode === c.code).length
  return `- ${c.name}: ${count} photos`
}).join('\n')}

Generated by Clystcore Surveyors
`

  zip.file('survey_info.txt', surveyInfo)

  // Generate and download
  const content = await zip.generateAsync({ type: 'blob' })
  const filename = generatePhotosZipFilename(survey)
  saveAs(content, filename)

  return filename
}

/**
 * Download a single photo
 */
export function downloadPhoto(photo, category) {
  const blob = base64ToBlob(photo.imageData)
  const filename = `${sanitizeFilename(category?.name || 'photo')}_${photo.id}.jpg`
  saveAs(blob, filename)
}

export default {
  generateWordDocument,
  downloadPhotosAsZip,
  downloadPhoto
}
